
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { InterviewManagementFacade } from '@/services/interview';
import { toast } from 'sonner';
import { Interview } from '@/types/interview';

export const useInterviewManagement = () => {
  const queryClient = useQueryClient();
  
  const { data: pendingInterviews, isLoading, error, refetch } = useQuery<Interview[]>({
    queryKey: ['pendingInterviews'],
    queryFn: () => InterviewManagementFacade.getPendingInterviews(),
    initialData: [],
    meta: {
      onError: (error: Error) => {
        toast.error(`Feil ved lasting av ventende intervjuer: ${error.message}`);
      },
    },
    refetchInterval: 5000,
    staleTime: 1000,
    refetchOnWindowFocus: true,
  });

  const handlePublish = async (interviewId: string) => {
    try {
      toast.dismiss('publishing');
      toast.loading('Publiserer intervju...', { id: 'publishing' });
      
      const result = await InterviewManagementFacade.publishInterview(interviewId);
      
      if (!result) {
        throw new Error('Publiseringsoperasjonen mislyktes');
      }
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
        queryClient.invalidateQueries({ queryKey: ['interviews'] }),
        queryClient.invalidateQueries({ queryKey: ['feederInterviews'] })
      ]);
      
      toast.success('Intervju publisert', { id: 'publishing' });
    } catch (error) {
      console.error('Feil ved publisering av intervju:', error);
      toast.error(`Kunne ikke publisere intervju: ${error instanceof Error ? error.message : 'Ukjent feil'}`, { id: 'publishing' });
    }
  };

  const handleReject = async (interviewId: string) => {
    try {
      toast.dismiss('rejecting');
      toast.loading('Avviser intervju...', { id: 'rejecting' });
      await InterviewManagementFacade.rejectInterview(interviewId);
      
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['pendingInterviews'] }),
        queryClient.invalidateQueries({ queryKey: ['interviews'] }),
        queryClient.invalidateQueries({ queryKey: ['feederInterviews'] })
      ]);
      
      toast.success('Intervju avvist', { id: 'rejecting' });
    } catch (error) {
      toast.error(`Kunne ikke avvise intervju: ${error instanceof Error ? error.message : 'Ukjent feil'}`, { id: 'rejecting' });
    }
  };
  
  const handleManualRefresh = async () => {
    toast.info('Oppdaterer intervjuer...');
    await refetch();
    toast.success('Intervjuer oppdatert');
  };

  return {
    pendingInterviews,
    isLoading,
    error,
    handlePublish,
    handleReject,
    handleManualRefresh
  };
};
